import { useState, useRef } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import { useCreditConfig } from '../../../../contexts/CreditConfigContext';
import { generateVideo, pollVideoStatus } from '../../../../services/videoGenerationService';
import { handleUnifiedError } from '../../../../utils/unifiedErrorHandler';
import { useVideoOptions } from '../useVideoOptions';

export const useImageToVideo = ({ onResult, onGeneratingChange }) => {
  const { t } = useI18n();
  const { getVideoGenerationCost } = useCreditConfig();
  const [file, setFile] = useState(null);
  const [keyframe, setKeyframe] = useState(false);
  const [startFrame, setStartFrame] = useState(null);
  const [endFrame, setEndFrame] = useState(null);
  const [prompt, setPrompt] = useState('');
  const {
    model,
    setModel,
    videoMode,
    setVideoMode,
    aspectRatio,
    setAspectRatio,
    videoResolution,
    setVideoResolution,
    videoDuration,
    setVideoDuration,
    modelOptions,
    videoModeOptions,
    aspectRatioOptions,
    videoResolutionOptions,
    videoDurationOptions
  } = useVideoOptions();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });

  const inputRef = useRef(null);
  const inputStartRef = useRef(null);
  const inputEndRef = useRef(null);

  const validateImage = (f) => {
    if (!f) return false;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const okType = allowed.includes(f.type);
    const okSize = f.size <= 10 * 1024 * 1024;
    if (!okType || !okSize) {
      setErrorModal({
        open: true,
        title: t('common.warning'),
        message: `${t('imageService.unsupportedFormat')}\n${t('imageService.fileTooLarge')}`
      });
      return false;
    }
    return true;
  };

  const onPick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!validateImage(f)) {
      if (inputRef.current) inputRef.current.value = null;
      return;
    }
    setFile(f);
  };

  const onPickStart = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!validateImage(f)) {
      if (inputStartRef.current) inputStartRef.current.value = null;
      return;
    }
    setStartFrame(f);
  };

  const onPickEnd = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!validateImage(f)) {
      if (inputEndRef.current) inputEndRef.current.value = null;
      return;
    }
    setEndFrame(f);
  };

  const openPreview = (fileObj) => {
    if (!fileObj) return;
    setPreviewUrl(URL.createObjectURL(fileObj));
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const credits = getVideoGenerationCost({
    model,
    videoMode,
    videoResolution,
    videoDuration
  });
  const hasMode = Array.isArray(videoModeOptions) && videoModeOptions.length > 0 ? !!videoMode : true;
  const hasOptions = !!(model && aspectRatio && videoResolution && videoDuration && hasMode);
  const canGenerate = hasOptions && (keyframe ? !!(startFrame && endFrame && prompt.trim()) : !!(file && prompt.trim()));

  const handleGenerate = async () => {
    if (isGenerating) return;
    if (keyframe) {
      if (!(startFrame && endFrame) || !prompt.trim()) {
        setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: t('videoGeneration.veo.common.promptAndStartEndRequired') });
        return;
      }
    } else if (!file || !prompt.trim()) {
      setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: t('videoGeneration.veo.common.promptAndImageRequired') });
      return;
    }

    setIsGenerating(true);
    if (typeof onGeneratingChange === 'function') onGeneratingChange(true);
    setGenerationProgress(0);
    const basis = 90000;
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.min(98, Math.round((elapsed / basis) * 100));
      setGenerationProgress(percent);
    }, 200);

    try {
      const gen = await generateVideo({
        type: keyframe ? 'KEY_IMAGE_TO_VIDEO' : 'IMAGE_TO_VIDEO',
        prompt,
        model,
        videoMode,
        aspectRatio,
        videoResolution,
        videoDuration,
        startImage: keyframe ? startFrame : file,
        endImage: keyframe ? endFrame : undefined
      });
      let final = null;
      if (gen && gen.id) {
        final = await pollVideoStatus(gen.id, { intervalMs: 2000, timeoutMs: 300000 });
      }
      clearInterval(timer);
      if (final && final.videoUrl) {
        setGenerationProgress(100);
        if (typeof onResult === 'function') {
          onResult({ src: final.videoUrl, payload: final.payload });
        }
      } else {
        throw new Error('NO_RESULT');
      }
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress(0);
        if (typeof onGeneratingChange === 'function') onGeneratingChange(false);
      }, 400);
    } catch (e) {
      clearInterval(timer);
      setIsGenerating(false);
      if (typeof onGeneratingChange === 'function') onGeneratingChange(false);
      if (e?.code === 'FAILED' || e?.code === 'TIMEOUT' || e?.code === 'REQUEST_FAILED') {
        const msg = e?.message || t('videoGeneration.veo.common.requestFailed');
        setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: msg });
      } else {
        handleUnifiedError(e, t, {
          showToast: false,
          onError: (errorInfo) => {
            setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: errorInfo.message });
          }
        });
      }
    }
  };

  return {
    file, setFile,
    keyframe, setKeyframe,
    startFrame, setStartFrame,
    endFrame, setEndFrame,
    prompt, setPrompt,
    model, setModel,
    videoMode, setVideoMode,
    aspectRatio, setAspectRatio,
    videoResolution, setVideoResolution,
    videoDuration, setVideoDuration,
    modelOptions,
    videoModeOptions,
    aspectRatioOptions,
    videoResolutionOptions,
    videoDurationOptions,
    previewUrl, openPreview, closePreview,
    isGenerating,
    generationProgress,
    errorModal, setErrorModal,
    inputRef,
    inputStartRef,
    inputEndRef,
    credits,
    canGenerate,
    onPick,
    onPickStart,
    onPickEnd,
    handleGenerate
  };
};
